const accountTypes = new Set(["Individual", "Business"]);
const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/u;
const controlCharacters = /[\u0000-\u001f\u007f]/u;
const sqlRowVersion = /^[A-Za-z0-9+/]{11}=$/u;

function required(value, maximum, label) {
  const normalized = String(value ?? "").trim();
  if (!normalized) throw new Error(`${label} is required.`);
  if (normalized.length > maximum || controlCharacters.test(normalized))
    throw new Error(`${label} must use no more than ${maximum} printable characters.`);
  return normalized;
}

function optional(value, maximum, label) {
  if (value == null || String(value).trim() === "") return null;
  const normalized = String(value).trim();
  if (normalized.length > maximum || controlCharacters.test(normalized))
    throw new Error(`${label} must use no more than ${maximum} printable characters.`);
  return normalized;
}

export function normalizeBillingProfile(input) {
  const accountType = String(input?.accountType ?? "").trim();
  if (!accountTypes.has(accountType)) throw new Error("Choose an Individual or Business billing account.");
  const billingEmail = required(input?.billingEmail, 320, "Billing email").toLowerCase();
  if (!/^[^\s@]+@[^\s@]+$/u.test(billingEmail)) throw new Error("Enter a valid billing email address.");
  const countryCode = required(input?.countryCode, 2, "Country code").toUpperCase();
  if (!/^[A-Z]{2}$/u.test(countryCode)) throw new Error("Country code must contain two letters.");
  let taxRegionCode = optional(input?.taxRegionCode, 2, "GST state code")?.toUpperCase() ?? null;
  let gstin = optional(input?.gstin, 15, "GSTIN")?.toUpperCase() ?? null;
  if (countryCode === "IN") {
    if (!/^[0-9]{2}$/u.test(taxRegionCode ?? ""))
      throw new Error("Indian billing addresses require the two-digit GST state or union-territory code.");
    if (gstin && (!gstinPattern.test(gstin) || !gstin.startsWith(taxRegionCode)))
      throw new Error("GSTIN must use the standard 15-character format and match the billing state code.");
  } else {
    if (gstin) throw new Error("GSTIN can be recorded only for an Indian billing address.");
    taxRegionCode = null;
    gstin = null;
  }
  if (input?.detailsConfirmedByAccountCreator !== true)
    throw new Error("Confirm that the billing details are complete and accurate.");
  return {
    accountType,
    billingName: required(input?.billingName, 256, "Billing name"),
    billingEmail,
    billingPhone: optional(input?.billingPhone, 32, "Billing phone"),
    addressLine1: required(input?.addressLine1, 256, "Address line 1"),
    addressLine2: optional(input?.addressLine2, 256, "Address line 2"),
    city: required(input?.city, 128, "City"),
    stateOrProvince: required(input?.stateOrProvince, 128, "State or province"),
    postalCode: required(input?.postalCode, 32, "Postal code"),
    countryCode,
    taxRegionCode,
    gstin,
    detailsConfirmedByAccountCreator: true,
  };
}

export function billingProfileDraft(profile = {}) {
  return {
    accountType: accountTypes.has(profile.accountType) ? profile.accountType : "Individual",
    billingName: String(profile.billingName ?? ""),
    billingEmail: String(profile.billingEmail ?? ""),
    billingPhone: String(profile.billingPhone ?? ""),
    addressLine1: String(profile.addressLine1 ?? ""),
    addressLine2: String(profile.addressLine2 ?? ""),
    city: String(profile.city ?? ""),
    stateOrProvince: String(profile.stateOrProvince ?? ""),
    postalCode: String(profile.postalCode ?? ""),
    countryCode: String(profile.countryCode ?? "IN").toUpperCase(),
    taxRegionCode: String(profile.taxRegionCode ?? ""),
    gstin: String(profile.gstin ?? ""),
    detailsConfirmedByAccountCreator: false,
  };
}

export function createBillingProfileUpdate(input, rowVersion) {
  const version = rowVersion == null ? null : String(rowVersion).trim();
  if (version !== null && !sqlRowVersion.test(version))
    throw new Error("Refresh the billing profile before saving because its version is invalid.");
  return { profile: normalizeBillingProfile(input), rowVersion: version };
}
